import { AuxiliaryObjectType, MeshType } from "~/src/utils/shape-type";
import { Injectable, Injector } from "@angular/core";
import { Mesh, MeshBasicMaterial, Vector2, Vector3, Box3, Color, ColorRepresentation } from "three";
import { ComponentInteractionService } from "../component-interaction/component-interaction.service";
import { v4 as uuidv4 } from "uuid";
import { HelperService } from "../../shared/helpers/helper.service";
import { SceneManagerService } from "../scene-manager/scene-manager.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Object3DUserData } from "../../../../../utils/shape";
import { CanvasConfig } from "../../../../../utils/canvas-configuration";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { Action, UndoRedoAction } from "~/src/app/shared/models/undoRedo.model";
import { UndoRedoService } from "../undoRedo/undo-redo.service";
import { TranslateService } from "@ngx-translate/core";
import { Object3D, Intersection } from "three";
import { InteractionService } from "../../shared/helpers/interaction.service";
import { MatSnackBarMessages } from "../../shared/mat-snackbar/mat-snackbar.model";
import { environment } from "~/src/environments/environment";

@Injectable({
    providedIn: "root",
})
export class CopyPasteService {
    public location = new Vector3();
    public copiedMeshes: Mesh[] = []; // Array to hold copied meshes
    public clickMouseEvent!: MouseEvent;
    private selectedShape = "";
    public selectedColor: ColorRepresentation | undefined;
    private intersects: Intersection<Object3D>[] = [];
    private pasteIntersect: Intersection<Object3D> | null = null;
    public selectedShapes: Mesh[] = [];
    private _interactionService!: InteractionService;

    constructor(
        private componentInteractionService: ComponentInteractionService,
        private helperService: HelperService,
        private snackBar: MatSnackBar,
        private sceneManagerService: SceneManagerService,
        private undoRedoService: UndoRedoService,
        private translate: TranslateService,
        private injector: Injector,
    ) {
        this.componentInteractionService.getInterceptInfo().subscribe((intersects) => {
            this.intersects = intersects;
        });

        this.componentInteractionService.getSeletedShape().subscribe((shape) => {
            if (shape) {
                this.selectedShape = shape;
            }
        });

        this.componentInteractionService.meshSelections$.subscribe((selections) => {
            this.selectedShapes = selections.map((selection) => selection.mesh);
        });

        window.addEventListener("keydown", this.handleKeydown.bind(this));
        window.addEventListener("click", (event) => {
            if (event.shiftKey && event.metaKey) {
                this.clickMouseEvent = event;

                if (this.copiedMeshes.length) {
                    if (this.intersects.length > 0) {
                        this.pasteIntersect = this.intersects[0];
                    }
                }
            }
        });
    }
    get interactionService(): InteractionService {
        if (!this._interactionService) {
            this._interactionService = this.injector.get(InteractionService);
        }
        return this._interactionService;
    }

    public handleKeydown(event: KeyboardEvent): void {
        // Handle the "Command + C" or "Ctrl + C" for copying
        if ((event.metaKey || event.ctrlKey) && event.key === "c") {
            if (this.selectedShapes && this.selectedShapes.length > 0) {
                this.copyMultipleObjects(this.selectedShapes);
            }
        }

        // Handle the "Command + V" or "Ctrl + V" for pasting
        if ((event.metaKey || event.ctrlKey) && event.key === "v") {
            if (this.pasteIntersect) {
                if (this.copiedMeshes.length > 0 && this.clickMouseEvent) {
                    this.handleMouseClick();
                }
            }
        }
    }

    public handleMouseClick(): void {
        if (this.copiedMeshes.length > 0) {
            if (this.pasteIntersect) {
                const data = this.helperService.calculatePosition(
                    [this.pasteIntersect],
                    this.location,
                    false,
                    this.copiedMeshes[0].name,
                );
                const gridPosition = this.helperService.getNearestGridCords(new Vector2(data?.loc.x, data?.loc.z));
                if (gridPosition) {
                    const newLocation = new Vector3(gridPosition.x, data?.loc.y, gridPosition.y);
                    if (
                        newLocation.z >= -CanvasConfig.maxMinConstant &&
                        newLocation.z <= CanvasConfig.maxMinConstant &&
                        newLocation.x >= -CanvasConfig.maxMinConstant &&
                        newLocation.x <= CanvasConfig.maxMinConstant
                    ) {
                        if (data?.invalidJoin) {
                            this.snackBar.open(
                                this.getTranslation("designer.invalidMessage.invalidJoin"),
                                SnackBarConfig.Actions.CLOSE,
                                {
                                    duration: SnackBarConfig.Duration.MEDIUM,
                                },
                            );
                        } else if (data?.invalidPointGeneration) {
                            this.snackBar.open(
                                this.getTranslation("designer.invalidMessage.invalidPointJoin"),
                                SnackBarConfig.Actions.CLOSE,
                                {
                                    duration: SnackBarConfig.Duration.MEDIUM,
                                },
                            );
                        } else if (!data?.invalidJoin && !data?.invalidPointGeneration) {
                            this.pasteObjectsAtNewLocation(this.copiedMeshes as Mesh[], newLocation);
                        }
                    } else {
                        this.snackBar.open(
                            this.getTranslation("designer.invalidMessage.invalidPointGeneration"),
                            SnackBarConfig.Actions.CLOSE,
                            {
                                duration: SnackBarConfig.Duration.MEDIUM,
                            },
                        );
                    }
                }
            }
        }
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    public copyMultipleObjects(meshes: Mesh[]): void {
        const allmeshesInScene = this.sceneManagerService.getMeshes();
        if (this.interactionService.checkDuplicateOverlap(allmeshesInScene)) {
            return;
        }
        this.copiedMeshes = [];
        this.copiedMeshes = meshes.map((mesh) => mesh.clone()); // Clone all selected meshes
    }

    public calculateGroupCenter(meshes: Mesh[]): Vector3 {
        const boundingBox = new Box3();
        const center = new Vector3();

        // Expand bounding box to include all selected meshes
        meshes.forEach((mesh) => boundingBox.expandByObject(mesh));

        // Calculate the center of the bounding box
        boundingBox.getCenter(center);
        return center;
    }

    public pasteObjectsAtNewLocation(meshes: Mesh[], newLocation: Vector3, isduplicate = false): Mesh[] {
        //checking max-mesh-length should not exceed 6000
        if (this.sceneManagerService.getMeshes().length + meshes.length > environment.MAX_MESHES) {
            this.snackBar.open(MatSnackBarMessages.MESH_LIMIT_REACHED, SnackBarConfig.Actions.OKAY, {
                duration: SnackBarConfig.Duration.MEDIUM,
            });
            return [];
        }
        if (!meshes?.length) return [];

        let referenceObject = meshes[0];

        const nextReference = this.helperService.getReferenceMesh(referenceObject, meshes);

        if (nextReference) {
            referenceObject = nextReference;
        }

        const newPositions = this.calculateNewPositions(meshes, newLocation, referenceObject.position);

        if (this.isOutOfBounds(newPositions)) {
            this.showSnackBar("designer.invalidMessage.invalidPointGeneration", SnackBarConfig.Duration.MEDIUM);
            return [];
        }

        if (!this.canPasteHere(meshes)) return [];

        if (this.checkCollision(newPositions)) {
            this.showSnackBar("designer.invalidMessage.collisionDetected", SnackBarConfig.Duration.MEDIUM);
            return [];
        }

        const pastedObjects = this.cloneAndPlaceObjects(meshes, newLocation, referenceObject.position);
        this.pasteIntersect = null;

        const action: UndoRedoAction = {
            actionType: isduplicate ? Action.Duplicate : Action.CopyPaste,
            meshes: pastedObjects,
        };

        this.undoRedoService.addToUndo(action);
        return pastedObjects;
    }

    private getAdjustedGroupCenter(meshes: Mesh[]): Vector3 {
        const center = this.calculateGroupCenter(meshes);
        const lowestY = Math.min(...meshes.map((mesh) => mesh.position.y));
        center.setY(lowestY);
        return center;
    }

    private calculateNewPositions(meshes: Mesh[], newLocation: Vector3, center: Vector3): Vector3[] {
        return meshes.map((mesh) => newLocation.clone().add(new Vector3().subVectors(mesh.position, center)));
    }

    private isOutOfBounds(positions: Vector3[]): boolean {
        return positions.some(
            (pos) => Math.abs(pos.x) > CanvasConfig.maxMinConstant || Math.abs(pos.z) > CanvasConfig.maxMinConstant,
        );
    }

    private canPasteHere(meshes: Mesh[]): boolean {
        const isPastingOnPlane = !this.pasteIntersect || this.pasteIntersect.object.name === AuxiliaryObjectType.Plane;

        if (!isPastingOnPlane && !this.isSingleConnectedStructure(meshes)) {
            this.showSnackBar("designer.invalidMessage.cannotPlaceDisconnectedStructure", SnackBarConfig.Duration.LONG);
            this.componentInteractionService.setSelectedShape(this.selectedShape as MeshType);
            return false;
        }

        return true;
    }

    private showSnackBar(messageKey: string, duration: number): void {
        this.snackBar.open(this.getTranslation(messageKey), SnackBarConfig.Actions.CLOSE, { duration });
    }

    private cloneAndPlaceObjects(meshes: Mesh[], newLocation: Vector3, center: Vector3): Mesh[] {
        const pastedObjects: Mesh[] = [];
        meshes.forEach((originalMesh) => {
            const cloned = originalMesh.clone();

            cloned.uuid = uuidv4();

            const offset = new Vector3().subVectors(originalMesh.position, center);
            const updatedPos = newLocation.clone().add(offset);
            const nearestGrid = this.helperService.getNearestGridCords(new Vector2(updatedPos.x, updatedPos.z));
            cloned.position.set(nearestGrid.x, updatedPos.y, nearestGrid.y);

            cloned.userData[Object3DUserData.cellReference] = {};
            if (Math.floor(cloned.position.y) % 10 !== 5) {
                const { row, column, cell } = this.helperService.calculateRowAndColumn(cloned);
                cloned.userData[Object3DUserData.cellReference] = { row, column, cell };
            }
            this.updateInitialPosition(cloned);

            this.restoreMaterialColors(cloned);
            this.sceneManagerService.addObjectsToScene([cloned]);
            pastedObjects.push(cloned);
        });

        return pastedObjects;
    }

    private restoreMaterialColors(mesh: Mesh): void {
        const originalColors: number[] = mesh.userData[Object3DUserData.originalColors];
        const originalMat = (mesh as Mesh).material;

        if (Array.isArray(originalMat)) {
            const updatedMats = originalMat.map((mat, i) => {
                const newMat = mat.clone();
                if (newMat instanceof MeshBasicMaterial && originalColors[i] !== undefined) {
                    newMat.color = new Color(originalColors[i]);
                }
                return newMat;
            });
            (mesh as Mesh).material = updatedMats;
        } else if (originalMat instanceof MeshBasicMaterial && originalColors?.[0] !== undefined) {
            const newMat = originalMat.clone();
            newMat.color = new Color(originalColors[0]);
            (mesh as Mesh).material = newMat;
        }
    }

    private updateInitialPosition(draggedMesh: Mesh): void {
        const initial = draggedMesh;
        if (!(initial.userData[Object3DUserData.initialPosition] instanceof Vector3)) {
            initial.userData[Object3DUserData.initialPosition] = new Vector3();
        }
        initial.userData[Object3DUserData.initialPosition].copy(initial.position);
    }

    //  method for collision detection
    private checkCollision(newPositions: Vector3[]): boolean {
        const gridSize = 20;

        return newPositions.some((newPos) => {
            // Round the position to the nearest grid cell
            const roundedPosition = newPos.clone();
            roundedPosition.x = Math.round(roundedPosition.x / gridSize) * gridSize;
            roundedPosition.z = Math.round(roundedPosition.z / gridSize) * gridSize;

            // Check if this position is already occupied by another object in the scene
            return this.sceneManagerService.scene.children.some((sceneObject) => {
                if (sceneObject instanceof Mesh) {
                    const sceneObjectPos = sceneObject.position.clone();
                    sceneObjectPos.x = Math.round(sceneObjectPos.x / gridSize) * gridSize;
                    sceneObjectPos.z = Math.round(sceneObjectPos.z / gridSize) * gridSize;

                    // Check if the positions are the same (indicating a collision)
                    return sceneObjectPos.equals(roundedPosition);
                }
                return false;
            });
        });
    }

    private isSingleConnectedStructure(meshes: Mesh[]): boolean {
        const boundingBoxes = meshes.map((mesh) => new Box3().setFromObject(mesh));
        const visited = new Set<number>();
        const queue: number[] = [0];
        visited.add(0);

        while (queue.length > 0) {
            const current = queue.shift()!;
            for (let i = 0; i < boundingBoxes.length; i++) {
                if (!visited.has(i) && boundingBoxes[current].intersectsBox(boundingBoxes[i])) {
                    visited.add(i);
                    queue.push(i);
                }
            }
        }

        return visited.size === meshes.length;
    }

    public removeCopyObjects(): void {
        this.copiedMeshes = [];
    }
}
